import {graphqlRequestHandler} from 'constants/Api';
import {lshGETAUTH} from 'services/LocalStorageService';
import type {
  IBudgetLineInput,
  ILinkCreditApplicationInput,
  IReallocateLedgerEntryInput,
  IReplaceWalletCashflowPermissionsInput,
  ISetWalletSpendCategoryRuleInput,
  ISpendCategory,
  IUpsertWalletMonthlyBudgetsInput,
  IWalletCashflow,
  IWalletCashflowPermission,
  IWalletMonthlyBudget,
  IWalletSpendCategoryRule,
  IYearMonth,
} from './walletCashflowTypes';
import {
  lshAssertNoUnmappedWalletCashflowCosts,
  lshBuildBudgetLinesFromApplicationCosts,
} from './walletCashflowCategoryMapping';

const SPEND_CATEGORY_FIELDS = `
  id
  code
  label
  usageKind
  active
`;

const WALLET_MONTHLY_BUDGET_FIELDS = `
  id
  periodYear
  periodMonth
  allocatedAmount
  consumedAmount
  remainingAmount
  spendCategory {
    ${SPEND_CATEGORY_FIELDS}
  }
`;

const WALLET_CASHFLOW_FIELDS = `
  id
  walletId
  creditApplicationRefId
  totalAllocatedAmount
  periodStart
  periodEnd
  description
  linkedAt
  creditApplicationRef {
    id
    sourceSystem
    applicationId
    approvedAt
  }
  budgets {
    ${WALLET_MONTHLY_BUDGET_FIELDS}
  }
`;

function lshAuthHeaders(): Promise<Record<string, string>> {
  return lshGETAUTH().then((token) => ({Authorization: token}));
}

function lshGql<T>(
  operationName: string,
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  return lshAuthHeaders().then(
    (headers) =>
      graphqlRequestHandler(
        operationName,
        query,
        variables,
        headers,
      ) as Promise<T>,
  );
}

export async function apiGetSpendCategories(
  activeOnly = true,
): Promise<ISpendCategory[]> {
  const query = `
    query spendCategories($activeOnly: Boolean) {
      spendCategories(activeOnly: $activeOnly) {
        ${SPEND_CATEGORY_FIELDS}
      }
    }
  `;
  return lshGql<ISpendCategory[]>('spendCategories', query, {activeOnly});
}

export async function apiGetWalletCashflows(
  walletId: string,
): Promise<IWalletCashflow[]> {
  const query = `
    query walletCashflows($walletId: ID!) {
      walletCashflows(walletId: $walletId) {
        ${WALLET_CASHFLOW_FIELDS}
      }
    }
  `;
  return lshGql<IWalletCashflow[]>('walletCashflows', query, {walletId});
}

export async function apiGetWalletMonthlyBudgets(
  period: IYearMonth,
): Promise<IWalletMonthlyBudget[]> {
  const query = `
    query walletCashflow($period: YearMonthInput!) {
      walletCashflow(period: $period) {
        ${WALLET_MONTHLY_BUDGET_FIELDS}
      }
    }
  `;
  return lshGql<IWalletMonthlyBudget[]>('walletCashflow', query, {period});
}

export async function apiCreateWalletCashflow(
  input: ILinkCreditApplicationInput,
): Promise<IWalletCashflow> {
  const mutation = `
    mutation createWalletCashflow($input: LinkCreditApplicationInput!) {
      createWalletCashflow(input: $input) {
        ${WALLET_CASHFLOW_FIELDS}
      }
    }
  `;
  return lshGql<IWalletCashflow>('createWalletCashflow', mutation, {input});
}

export async function apiLinkFdApplicationToWallet(
  applicationId: string,
  walletId?: number,
): Promise<{
  id: string;
  applicationWallet?: number;
  applicationWalletAccount?: unknown;
}> {
  const mutation = `
    mutation linkFDApplicationToWallet($applicationId: ID!, $walletId: Float) {
      linkFDApplicationToWallet(applicationId: $applicationId, walletId: $walletId) {
        id
        applicationWallet
        applicationWalletAccount {
          id
          customName
          accounts {
            id
            accountName
            accountNumber
            AvailableBalance
          }
        }
      }
    }
  `;
  return lshGql('linkFDApplicationToWallet', mutation, {
    applicationId,
    walletId,
  });
}

export async function apiUpsertWalletMonthlyBudgets(
  input: IUpsertWalletMonthlyBudgetsInput,
): Promise<IWalletMonthlyBudget[]> {
  const mutation = `
    mutation upsertWalletMonthlyBudgets($input: UpsertWalletMonthlyBudgetsInput!) {
      upsertWalletMonthlyBudgets(input: $input) {
        ${WALLET_MONTHLY_BUDGET_FIELDS}
      }
    }
  `;
  return lshGql<IWalletMonthlyBudget[]>(
    'upsertWalletMonthlyBudgets',
    mutation,
    {input},
  );
}

export async function apiReallocateLedgerEntry(
  input: IReallocateLedgerEntryInput,
): Promise<unknown> {
  const mutation = `
    mutation reallocateLedgerEntry($input: ReallocateLedgerEntryInput!) {
      reallocateLedgerEntry(input: $input) {
        id
        walletMonthlyBudgetId
        amount
        entryType
        note
        createdAt
      }
    }
  `;
  return lshGql('reallocateLedgerEntry', mutation, {input});
}

export async function apiReallocateBudget(input: {
  fromBudgetId: string;
  toBudgetId: string;
  amount: number;
}): Promise<IWalletMonthlyBudget[]> {
  const mutation = `
    mutation reallocateBudget($input: ReallocateBudgetInput!) {
      reallocateBudget(input: $input) {
        ${WALLET_MONTHLY_BUDGET_FIELDS}
      }
    }
  `;
  return lshGql<IWalletMonthlyBudget[]>('reallocateBudget', mutation, {input});
}

export async function apiSetWalletSpendCategoryRule(
  input: ISetWalletSpendCategoryRuleInput,
): Promise<IWalletSpendCategoryRule> {
  const mutation = `
    mutation setWalletSpendCategoryRule($input: SetWalletSpendCategoryRuleInput!) {
      setWalletSpendCategoryRule(input: $input) {
        id
        walletId
        purchaseRestrictionMode
        spendCategory {
          ${SPEND_CATEGORY_FIELDS}
        }
      }
    }
  `;
  return lshGql<IWalletSpendCategoryRule>(
    'setWalletSpendCategoryRule',
    mutation,
    {input},
  );
}

export async function apiGetWalletCashflowPermissions(): Promise<
  IWalletCashflowPermission[]
> {
  const query = `
    query walletCashflowPermissions {
      walletCashflowPermissions {
        id
        name
        description
      }
    }
  `;
  return lshGql<IWalletCashflowPermission[]>(
    'walletCashflowPermissions',
    query,
  );
}

export async function apiGetWalletCashflowPermissionsByUser(
  userId: string,
): Promise<IWalletCashflowPermission[]> {
  const query = `
    query walletCashflowPermissionsByUser($userId: ID!) {
      walletCashflowPermissionsByUser(userId: $userId) {
        id
        name
        description
      }
    }
  `;
  return lshGql<IWalletCashflowPermission[]>(
    'walletCashflowPermissionsByUser',
    query,
    {userId},
  );
}

export async function apiReplaceWalletCashflowPermissions(
  input: IReplaceWalletCashflowPermissionsInput,
): Promise<IWalletCashflowPermission[]> {
  const mutation = `
    mutation replaceWalletCashflowPermissions($input: ReplaceWalletCashflowPermissionsInput!) {
      replaceWalletCashflowPermissions(input: $input) {
        id
        name
        description
      }
    }
  `;
  return lshGql<IWalletCashflowPermission[]>(
    'replaceWalletCashflowPermissions',
    mutation,
    {input},
  );
}

export function lshBuildBudgetLinesFromFundingCosts(
  costs: Array<{label: string; cost: number}>,
  categories: ISpendCategory[],
): IBudgetLineInput[] {
  const {lines, unmappedItems} = lshBuildBudgetLinesFromApplicationCosts(
    costs,
    categories,
  );
  lshAssertNoUnmappedWalletCashflowCosts(unmappedItems);
  return lines;
}
